import { isEqual } from 'lodash';
import React, { useEffect } from 'react';
import { PiTrashDuotone } from 'react-icons/pi';
import { Button } from 'rizzui';
import CustomSearchableEntities from '../../../components/forms/CustomSearchableEntities';
import CustomSearchInput from '../../../components/forms/CustomSearchInput';
import CustomSelect from '../../../components/forms/CustomSelect';
import CustomStatusSelect from '../../../components/forms/CustomStatusSelect';
import CustomTextDelayInput from '../../../components/forms/CustomTextDelayInput';
import DateField from '../../../components/forms/DateField';
import FormLabel from '../../../components/forms/FormLabel';
import SelectProvinceFilter from '../../../components/forms/SelectProvinceFilter';
import useContractCampaign from '../../../hooks/api-calls/contract/useContractCampaign';
import useContractCommercial from '../../../hooks/api-calls/contract/useContractCommercial';
import useContractCorporation from '../../../hooks/api-calls/contract/useContractCorporation';
import useContractGroups from '../../../hooks/api-calls/useContractGroups';
import useFundaeGroupings from '../../../hooks/api-calls/useFundaeGroupings';
import useProducts from '../../../hooks/api-calls/useProducts';
import useProductTypes from '../../../hooks/api-calls/useProductTypes';
import { FilterOptions } from '../../../hooks/useFilters';
import useContractUserNetworks from '../../../hooks/api-calls/useContractUserNetworks';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';

interface ContractsFiltersProps {
    updateFilters: (filters: any) => void
    resetFilters: (limit: any) => void
    filters: FilterOptions
    showStatusFilter?: boolean
}

const ContractsFilters: React.FC<ContractsFiltersProps> = ({ filters, updateFilters, resetFilters, showStatusFilter = true }) => {

    const { getContractGroupsList, updateContractGroupsHookFilters } = useContractGroups();
    const { fetchContractCommercial } = useContractCommercial();
    const { getUserNetworksList, updateUserNetworksHookFilters } = useContractUserNetworks();
    const { getContractCorporationList, updateContractCorporationHookFilters } = useContractCorporation();
    const { getContractCampaignList, updateContractCampaignHookFilters } = useContractCampaign();
    const { getProductTypesList } = useProductTypes({ apply_contract_product_type_scope: true });
    const { fetchProducts } = useProducts();
    const { getFundaeGroupingsList, updateFundaeGroupingsHookFilters } = useFundaeGroupings();
    const { userCan } = usePrivilege();

    useEffect(() => {
        updateFundaeGroupingsHookFilters({
            organising_corporation_id: filters.filter_filters?.organising_corporation || null,
        });
    }, [filters.filter_filters?.organising_corporation, updateFundaeGroupingsHookFilters]);

    return (
        <>
            {userCan('admin_company', 'companies') && (
                <CustomSelect
                    isSearchable
                    id={'company_active'}
                    label="Estado compañía"
                    options={[
                        { label: 'Activas', value: true },
                        { label: 'Desactivadas', value: false },
                        { label: 'Todas', value: 'all' }
                    ]}
                    onChange={(e: any) => {
                        const value = e.value === 'all' ? 'all' : e.value;
                        updateFilters({ company_active: value });
                    }}
                    value={
                        filters.filter_filters?.company_active === 'all'
                            ? { label: 'Todas', value: 'all' }
                            : filters.filter_filters?.company_active === false
                                ? { label: 'Desactivadas', value: false }
                                : { label: 'Activas', value: true }
                    }
                />
            )}

            <CustomSelect
                isSearchable
                id={'organising_corporation_active'}
                label="Estado empresa fiscal"
                options={[
                    { label: 'Activas', value: true },
                    { label: 'Desactivadas', value: false },
                    { label: 'Todas', value: 'all' }
                ]}
                onChange={(e: any) => {
                    const value = e.value === 'all' ? 'all' : e.value;
                    updateFilters({ organising_corporation_active: value });
                }}
                value={
                    filters.filter_filters?.organising_corporation_active === 'all'
                        ? { label: 'Todas', value: 'all' }
                        : filters.filter_filters?.organising_corporation_active === false
                            ? { label: 'Desactivadas', value: false }
                            : { label: 'Activas', value: true }
                }
            />

            <CustomSearchableEntities
                id="contract_group"
                label="Grupo"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    updateContractGroupsHookFilters({ finder: searchText });
                    return getContractGroupsList();
                }}
                permissions={[
                    {
                        group: 'contracts',
                        action: 'admin_contracts'
                    }
                ]}
            />

            <CustomTextDelayInput
                id="grouping"
                label="Agrupación"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomTextDelayInput
                id="group_code"
                label="Código de Grupo"
                filters={filters}
                updateFilters={updateFilters}
            />

            <CustomSelect
                isSearchable
                isMulti
                id={'fundae_groupings'}
                label="Agrupación Fundae"
                options={getFundaeGroupingsList()}
                onChange={(e: any) => {
                    const selectedValues = Array.isArray(e) ? e.map((item: any) => item.value) : [];
                    updateFilters({ fundae_groupings: selectedValues.length > 0 ? selectedValues : null });
                }}
                value={
                    filters.filter_filters?.fundae_groupings
                        ? getFundaeGroupingsList().filter((item: any) => filters.filter_filters?.fundae_groupings.includes(item.value))
                        : null
                }
            />

            { userCan('get_product_types_for_select', 'product_types') && (    
                <CustomSelect
                    isSearchable
                    isMulti
                    id={'product_type'}
                    label="Tipo de Producto"
                    options={getProductTypesList()}
                    onChange={(e: any) => {
                        const selectedValues = e.map((item: any) => item.value);
                        updateFilters({ product_type: selectedValues.length > 0 ? selectedValues : null });
                    }}
                    value={
                        filters.filter_filters?.product_type
                            ? getProductTypesList().filter((item: any) => filters.filter_filters?.product_type.includes(item.value))
                            : null
                    }
                />
            )}

            {userCan('get_products_for_select', 'products' ) &&
                <CustomSearchableEntities
                    id="product"
                    label="Producto"
                    filters={filters}
                    updateFilters={updateFilters}
                    fetchOptions={async (searchText: string | null) => {
                        return fetchProducts({ search_text: searchText }) as Promise<any>;
                    }}
                    permissions={[
                        {
                            group: 'products',
                            action: 'get_products_for_select'
                        }
                    ]}
                />
            }

            <CustomSearchableEntities
                id="commercial"
                label="Comercial"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    return fetchContractCommercial({ finder: searchText});
                }}
                permissions={[
                    {
                        group: 'contracts',
                        action: 'admin_contracts'
                    },
                    {
                        group: 'commercial_network',
                        action: 'admin_all_commercial_network_contracts'
                    }
                ]}
            />

            <CustomSearchableEntities
                id="commercial_network"
                label="Red Comercial"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    updateUserNetworksHookFilters({ finder: searchText });
                    return getUserNetworksList();
                }}
                permissions={[
                    {
                        group: 'contracts',
                        action: 'admin_contracts'
                    },
                    {
                        group: 'commercial_network',
                        action: 'admin_all_commercial_network_contracts'
                    }
                ]}
            />

            <CustomSearchableEntities
                id="organising_corporation"
                label="Empresa Fiscal"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    updateContractCorporationHookFilters({ finder: searchText });
                    return getContractCorporationList();
                }}
                permissions={[
                    {
                        group: 'contracts',
                        action: 'admin_contracts'
                    }
                ]}
            />

            <CustomSearchableEntities
                id="campaign"
                label="Campaña"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    updateContractCampaignHookFilters({ finder: searchText });
                    return getContractCampaignList();
                }}
                permissions={[
                    {
                        group: 'contracts',
                        action: 'admin_contracts'
                    }
                ]}
            />

            <SelectProvinceFilter label='Provincia del Cliente' id='client_province' filters={filters} updateFilters={updateFilters} />

            <FormLabel label='CNAE del Cliente'>
                <CustomSearchInput onSearch={(e) => updateFilters({ client_cnae: e })} defaultValue={filters.filter_filters?.client_cnae || ''} />
            </FormLabel>

            {showStatusFilter && (
                <CustomStatusSelect
                    id="custom_status"
                    label="Estados"
                    selectedStateSearchValue={filters.filter_filters?.custom_status?.current}
                    selectedStateEntityValues={filters.filter_filters?.custom_status?.entity}
                    selectedStatesValues={filters.filter_filters?.custom_status?.statuses}
                    startDate={filters.filter_filters?.custom_status?.startDate ? new Date(filters.filter_filters.custom_status.startDate) : null}
                    endDate={filters.filter_filters?.custom_status?.endDate ? new Date(filters.filter_filters.custom_status.endDate) : null}
                    onChange={(customStatus: any) => { updateFilters({ custom_status: customStatus }); }}
                    filterValue={filters.filter_filters?.custom_status}
                />
            )}

            <DateField
                isClearable
                className="w-full"
                label="Fecha de Creación"
                placeholderText="Selecciona las fechas"
                selectedDateField={filters.filter_filters?.between_dates?.type || "createdAt"}
                dateTypesOptions={[
                    { label: "Fecha de creación/alta", value: "createdAt" },
                    { label: "Fecha de firma", value: "signDate" },
                    { label: "Fecha de inicio", value: "productStartDate" },
                    { label: "Fecha de fin", value: "productEndDate" },
                ]}
                startDate={filters.filter_filters?.between_dates?.startDate ? new Date(filters.filter_filters.between_dates.startDate) : null}
                endDate={filters.filter_filters?.between_dates?.endDate ? new Date(filters.filter_filters.between_dates.endDate) : null}
                onChange={(date: any) => {
                    const filters_between_dates = filters.filter_filters?.between_dates;
                    const between_dates = date ? { startDate: date[0], endDate: date[1] } : null;

                    if (between_dates && !isEqual(filters_between_dates, between_dates)) {
                        updateFilters({ between_dates: { startDate: date[0], endDate: date[1], type: date[2] } });
                    } else if (!between_dates && filters_between_dates) {
                        updateFilters({ between_dates: null });
                    }
                }}
            />

            <Button
                size="sm"
                onClick={() => {
                    resetFilters(50);
                }}
                variant="flat"
                className="h-9 bg-gray-200/70"
            >
                <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" /> Limpiar
            </Button>
        </>
    );
};

export default ContractsFilters;
